import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Wallet, ArrowLeftRight, Receipt, LayoutGrid, Plus, Send, Banknote, ChartBar, Repeat, Settings } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // On the public Landing page, always render the anonymous header — never the account bar.
  const isLanding = location.pathname === "/";
  const effectiveUser = isLanding ? null : user;

  const dashLink = effectiveUser?.role === "merchant" ? "/merchant" : "/customer";

  const merchantNav = [
    { to: "/merchant", label: "Overview", icon: LayoutGrid },
    { to: "/merchant/links", label: "Payment Links", icon: Receipt },
    { to: "/analytics", label: "Analytics", icon: ChartBar },
    { to: "/withdrawals", label: "Withdraw", icon: Banknote },
    { to: "/wallet", label: "Wallet", icon: Wallet },
  ];
  const customerNav = [
    { to: "/customer", label: "Overview", icon: LayoutGrid },
    { to: "/send", label: "Send", icon: Send },
    { to: "/wallet", label: "Wallet", icon: Wallet },
    { to: "/subscriptions", label: "Subscriptions", icon: Repeat },
    { to: "/transactions", label: "Transactions", icon: ArrowLeftRight },
  ];
  const nav = effectiveUser?.role === "merchant" ? merchantNav : customerNav;

  return (
    <header data-testid="app-header" className="sticky top-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to={effectiveUser ? dashLink : "/"} data-testid="header-brand" className="flex items-center gap-2">
          <span className="font-display text-2xl tracking-tight silver-text">y <span className="text-xl">ℶα</span></span>
          <span className="text-xs uppercase tracking-[0.2em] text-muted-fg hidden sm:inline">INFINITE TIMES</span>
        </Link>

        {effectiveUser ? (
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => {
              const Icon = n.icon;
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                    active ? "text-gold" : "text-secondary-fg hover:text-white"
                  }`}
                >
                  <Icon size={14} />
                  {n.label}
                </Link>
              );
            })}
          </nav>
        ) : (
          <nav className="flex items-center gap-6">
            <Link to="/login" data-testid="nav-login" className="text-sm text-secondary-fg hover:text-white transition-colors">Sign in</Link>
            <Link to="/register" data-testid="nav-register" className="btn-gold text-sm">Get y ℶα</Link>
          </nav>
        )}

        {effectiveUser && (
          <div className="flex items-center gap-3">
            <Link to="/profile" data-testid="header-profile-link" className="hidden sm:flex items-center gap-3 group">
              <div className="text-right">
                <div data-testid="header-user-name" className="text-sm font-medium group-hover:text-gold transition-colors">{effectiveUser.name}</div>
                <div className="text-xs text-muted-fg uppercase tracking-widest">{effectiveUser.role}</div>
              </div>
              <div className="w-9 h-9 rounded-full bg-elevated border hairline-strong flex items-center justify-center font-display text-gold overflow-hidden">
                {effectiveUser.picture ? (
                  <img src={effectiveUser.picture} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  (effectiveUser.name || effectiveUser.email || "?").charAt(0).toUpperCase()
                )}
              </div>
            </Link>
            <Link to="/profile" data-testid="nav-profile" className="p-2 text-secondary-fg hover:text-white transition-colors" title="Profile">
              <Settings size={16} />
            </Link>
            <button
              data-testid="header-logout-btn"
              onClick={async () => { navigate("/"); await logout(); }}
              className="p-2 text-secondary-fg hover:text-white transition-colors"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
